<template>
  <div>
    <section class="section-create-account">
      <img
        class="trebel-logo-redesign only-desk"
        :src="`${publicPath}assets/images/trebel_logo.png`"
        :alt="$t('TREBEL')"
      />
      <div class="container">
        <div v-if="!formSubmitted" class="section-create-account-inner">
          <template v-if="currentStep <= maxStep">
            <div class="d-flex justify-content-between mt-56 mobile-w-312">
              <div class="progress-title">{{ $t("createYourAccount") }}</div>
              <div class="progress-badge">
                {{ currentStep }} / {{ maxStep }}
              </div>
            </div>

            <b-progress
              :value="progress"
              variant="yellow"
              height="4px"
              class="mobile-w-312"
            ></b-progress>
          </template>

          <form
            v-if="currentStep === 1"
            class="create-account-step step1 d-flex flex-column"
            @submit.prevent="onClickNext('step1')"
            data-vv-scope="step1"
          >
            <h3 class="step-title">{{ $t("whatsYourEmail") }}</h3>
            <div
              class="create-account-step-text step-sm-text"
              v-html="$t('weNeedYourEmailAddress')"
            ></div>
            <div class="form-controls-wrap">
              <div class="form-control-icon-wrap">
                <div class="input-field-floating">
                  <b-form-input
                    class="input-field-floating"
                    :class="{
                      'error-input': errors.has('step1.email'),
                      'valid-input': !errors.has('step1.email') && user.email
                    }"
                    v-model="user.email"
                    placeholder=""
                    v-validate="'required|email'"
                    data-vv-validate-on="none"
                    name="email"
                    data-vv-as="Email"
                  ></b-form-input>
                  <label>{{ $t("email") }}</label>
                  <img
                    v-if="user.email && user.email.length"
                    class="delete-inputed"
                    :src="`${publicPath}assets/images/svg/delete.svg`"
                    alt="delete"
                    @click="user.email = null"
                  />
                </div>
                <span
                  v-if="!errors.has('step1.email') && !error.message"
                  class="d-flex align-start helper-text"
                >
                  {{ $t("confirmAfterwards") }}
                </span>
                <div v-if="errors.has('step1.email')" class="invalid-field">
                  {{ errors.first("step1.email") }}
                </div>
                <div v-if="error.message" class="invalid-field ">
                  {{ error.message }}
                </div>
              </div>
            </div>
            <div class="btn-step-next-wrap mt-167 mb-40">
              <b-button
                type="submit"
                variant="yellow"
                block
                class="btn-step-next"
                :disabled="!user.email"
              >
                {{ $t("continue") }}
              </b-button>
            </div>
          </form>
          <!-- step 2 -->
          <form
            v-if="currentStep === 2"
            class="create-account-step step2 d-flex flex-column"
            @submit.prevent="onClickNext('step2')"
            data-vv-scope="step2"
          >
            <h3 class="step-title">{{ $t("chooseAPassword") }}</h3>
            <div class="form-controls-wrap">
              <div class="form-control-icon-wrap">
                <div class="input-field-floating">
                  <b-form-input
                    class="input-field-floating"
                    :class="{
                      'error-input': errors.has('step2.password'),
                      'valid-input':
                        !errors.has('step2.password') && user.password
                    }"
                    type="password"
                    v-model="user.password"
                    placeholder=""
                    v-validate="'required|min:6'"
                    data-vv-validate-on="none"
                    name="password"
                    :data-vv-as="$t('password')"
                    ref="password"
                  ></b-form-input>
                  <label> {{ $t("password") }}</label>
                  <img
                    v-if="user.password && user.password.length"
                    class="delete-inputed"
                    :src="`${publicPath}assets/images/svg/delete.svg`"
                    alt="delete"
                    @click="user.password = ''"
                  />
                </div>
                <span
                  v-if="!errors.has('step2.password')"
                  class="d-flex align-start helper-text"
                >
                  {{ $t("passwordContain") }}
                </span>
                <div v-if="errors.has('step2.password')" class="invalid-field">
                  {{ errors.first("step2.password") }}
                </div>
              </div>
            </div>

            <div class="btn-step-next-wrap mt-223 mb-40">
              <b-button
                type="submit"
                variant="yellow"
                block
                class="btn-step-next"
                :disabled="!user.password"
                >{{ $t("continue") }}</b-button
              >
            </div>
          </form>
          <!-- step 3 -->
          <form
            v-if="currentStep === 3"
            class="create-account-step step3 d-flex flex-column"
            @submit.prevent="onClickNext('step3')"
            data-vv-scope="step3"
          >
            <h3 class="step-title">{{ $t("whatsYourBirthYear") }}</h3>
            <div
              class="create-account-step-text step-sm-text p-0 mw-100"
              v-html="$t('weUseThisToKeepOurCommunitySafe')"
            ></div>
            <div class="form-controls-wrap">
              <div class="select-cs-wrap">
                <span class="select-cs-label">{{ $t("birthYear") }}</span>
                <multiselect
                  v-model="user.birthYear"
                  :options="options.birthYear"
                  :allow-empty="false"
                  :show-labels="false"
                  :placeholder="$t('select')"
                  :searchable="false"
                  open-direction="below"
                />
              </div>
            </div>
            <div class="btn-step-next-wrap mt-123 mobile-mt-223 mb-80">
              <b-button
                type="submit"
                variant="yellow"
                block
                class="btn-step-next"
                :disabled="!user.birthYear"
                >{{ $t("continue") }}</b-button
              >
              <b-button
                @click="skipQuestion('birthYear')"
                block
                class="btn-skip"
              >
                {{ $t("skip") }}
              </b-button>
            </div>
          </form>
          <!-- step 4 -->
          <form
            v-if="currentStep === 4"
            class="create-account-step step4 d-flex flex-column"
            @submit.prevent="onClickNext('step4')"
            data-vv-scope="step4"
          >
            <h3 class="step-title p-mobile-20">
              {{ $t("whatGenderDoYouIdentifyWith") }}
            </h3>
            <div class="create-account-step-text step-sm-text fs-16 p-0 mw-100">
              {{ $t("weUseGenderToPersonalize") }}
            </div>

            <div class="btn-gender-wrap">
              <b-button
                variant="gender"
                @click="setGender('Male')"
                :class="{ 'btn-gender-active': user.gender === 'Male' }"
                >{{ $t("man") }}</b-button
              >
              <b-button
                variant="gender"
                @click="setGender('Female')"
                :class="{ 'btn-gender-active': user.gender === 'Female' }"
                >{{ $t("woman") }}</b-button
              >
              <b-button
                variant="gender"
                @click="setGender('nonBinary')"
                :class="{ 'btn-gender-active': user.gender === 'nonBinary' }"
                >{{ $t("nonBinary") }}</b-button
              >
            </div>
            <div class="btn-step-next-wrap d-flex flex-column mt-93 mb-80">
              <b-button
                type="submit"
                variant="yellow"
                block
                class="btn-step-next"
                :disabled="!user.gender"
                >{{ $t("createAccount") }}</b-button
              >
              <b-button @click="skipQuestion('gender')" block class="btn-skip">
                {{ $t("skip") }}
              </b-button>
            </div>
          </form>
        </div>
        <div
          v-else
          class="d-flex flex-column justify-content-center align-items-center mt-40 mb-40"
        >
          <img
            width="103"
            height="31"
            class="only-mobile mb-40"
            :src="`${publicPath}assets/images/trebel_logo.png`"
            :alt="$t('TREBEL')"
          />
          <img
            :src="`${publicPath}assets/images/check_circle.png`"
            alt="Success"
            class="cicrle-checkmark mb-24"
          />

          <h3 class="completed-steps-title">
            {{ $t("registrationComplete") }}
          </h3>

          <template v-if="!isMobile">
            <div
              class="create-account-step-text completed-steps-desc p-0 mw-100"
            >
              {{ $t("downloadAppContinue") }}
            </div>
            <img class="qrcode" :src="qr" alt="Trebel QR" />

            <b-button
              variant="yellow"
              block
              class="btn-step-next w-282 fm-gotham"
              @click="redirectTo"
              >{{ $t("continue") }}</b-button
            >
          </template>
          <template v-else>
            <img
              v-if="deviceOs === 'android'"
              width="162"
              height="48"
              class="obj-contain"
              :src="`${publicPath}assets/images/android.png`"
              alt="Trebel QR"
              @click="openStore()"
            />
            <img
              v-if="deviceOs === 'ios'"
              width="162"
              height="48"
              class="obj-contain"
              :src="`${publicPath}assets/images/ios.png`"
              alt="Trebel QR"
              @click="openStore()"
            />
            <img
              v-if="deviceOs === 'huawei'"
              width="162"
              height="48"
              class="obj-contain"
              :src="`${publicPath}assets/images/other.png`"
              alt="Trebel QR"
              @click="openStore()"
            />
            <p class="download-info">
              {{ $t("downloadAppContinue") }}
            </p>
          </template>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { authComputed } from "@/store/helpers.js";
import Multiselect from "vue-multiselect";
import { detectMobileOS, isMobile } from "@/helpers/DeviceDetectWithOs.js";

export default {
  components: {
    Multiselect
  },
  data() {
    return {
      qr: null,
      QRCodeModuleLoaded: false,
      url:
        "https://app.adjust.com/uyyws65_mmma461?campaign=TrebelAI&adgroup=userreferral&creative=link&fallback=https%3A%2F%2Fhome.trebel.io%2Fid%2Fhome",
      formSubmitted: false,
      currentStep: 1,
      maxStep: 4,
      gender: "",
      user: {
        email: null,
        password: null,
        cpassword: null,
        gender: null,
        birthYear: null
      },
      token: null,
      options: {
        birthYear: []
      },
      error: {
        message: ""
      }
    };
  },
  computed: {
    ...authComputed,
    progress() {
      return Math.round(100 / this.maxStep) * this.currentStep;
    },
    isMobile() {
      return isMobile();
    },
    deviceOs() {
      return detectMobileOS();
    }
  },
  mounted() {
    if (this.$route.query.singIn && this.$route.query.singIn === "1") {
      this.formSubmitted = true;
    }
    this.fillBirthYears();
    this.getQRCode();
  },
  methods: {
    async getQRCode() {
      const options = {
        width: 180,
        height: 180,
        errorCorrectionLevel: "L",
        type: "image/webp",
        rendererOpts: {
          quality: 1
        }
      };
      this.qr = await window.QRCode.toDataURL(this.url, options);
    },
    onClickNext(scope) {
      this.$validator.validateAll(scope).then(result => {
        if (result) {
          if (scope === "step2") {
            this.register();
          } else if (scope === "step4" && this.currentStep === 4) {
            this.updateProfile();
          } else {
            this.currentStep++;
          }
        }
      });
    },
    skipQuestion(prop) {
      this.user[prop] = "";
      if (prop === "gender") {
        this.updateProfile();
      }
      this.currentStep++;
    },
    setGender(gender) {
      this.user.gender = gender;
    },
    fillBirthYears() {
      let year = new Date().getFullYear() - 12;
      for (let i = 0; i < 100; i++) {
        let yr = --year;
        this.options.birthYear.push(yr);
      }
    },
    register() {
      this.user.cpassword = this.user.password;
      this.$store
        .dispatch("register", this.user)
        .then(res => {
          res.status === "completed" ? this.currentStep++ : "";
        })
        .catch(error => {
          if (error) {
            this.currentStep = 1;
            this.error.message = error.response.data.error.message;
          }
        });
    },
    updateProfile() {
      if (this.getToken) {
        this.$store
          .dispatch("updateProfile", {
            gender: this.user.gender || "",
            birthYear: this.user.birthYear || ""
          })
          .then(res => {
            res ? (this.formSubmitted = true) : "";
          })
          .catch(error => {
            console.log(error.response.data.error.message);
          });
      }
    },
    redirectTo() {
      if (this.loggedIn) {
        window.location.href = this.$route.query.redirectTo
          ? this.$route.query.redirectTo
          : process.env.VUE_APP_REDIRECT;
      }
    },
    openStore() {
      window.location.replace(this.url);
    }
  },
  head: {
    script: [
      {
        hid: "QRCode",
        src:
          "https://jojotoo-static.oss-cn-shanghai.aliyuncs.com/resources/script/qrcode.min.js",
        defer: true,
        callback: () => {
          this.QRCodeModuleLoaded = true;
        }
      }
    ]
  }
};
</script>
